import { exportInvoices, getExportedInvoices } from "../../api/services/invoiceService";
import { InvoiceExportDetailGrouping } from "../../api/models/invoiceExportDetail";
import { useAsync } from "react-async";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import InvoiceExportDetailResponse from "../../api/responses/invoiceExportDetailResponse";
import InvoiceExportRequest from "../../api/requests/invoiceExportRequest";
import Layout from "../../components/layout";
import moment from "moment";

type ViewState = {
    invoiceExportRequest: InvoiceExportRequest;
};

const InvoiceExports = () => {
    const history = useHistory();
    const { state } = useLocation<ViewState>();
    const [exportedInvoiceGroupList, setExportedInvoiceGroupList] = useState<InvoiceExportDetailGrouping[]>();

    const navigateToExportDetails = (exportedInvoices: InvoiceExportDetailGrouping) => {
        history.push(`/accounting/invoice/exports/detail`, { exportedInvoices });
    }

    const navigateToExportEdit = (exportedInvoices: InvoiceExportDetailGrouping) => {
        history.push(`/accounting/invoice/exports/edit`, { exportedInvoices });
    }

    const handleExportDetailResponse = (response: InvoiceExportDetailResponse | null) => {
        const invoiceExportDetailGrouping: InvoiceExportDetailGrouping[] =
            response?.data?.exportedInvoicesByDate ?? [] as InvoiceExportDetailGrouping[];

        setExportedInvoiceGroupList(invoiceExportDetailGrouping);

        window.history.replaceState({}, ''); // Clear location state after export so request is not rerun
    }

    const handleExportDetailRejection = (error: Error) => {
        window.alert(error);
    }

    const exportInvoicesAsync = useAsync({
        deferFn: (args: any[]) => exportInvoices(args[0]),
        onResolve: handleExportDetailResponse,
        onReject: handleExportDetailRejection,
    });

    const getExportedInvoicesAsync = useAsync({
        deferFn: getExportedInvoices,
        onResolve: handleExportDetailResponse,
        onReject: handleExportDetailRejection,
    });

    useEffect(() => {
        if (state && state.invoiceExportRequest) {
            exportInvoicesAsync.run(state.invoiceExportRequest);
        } else {
            getExportedInvoicesAsync.run();
        }
    }, []);

    return (
        <Layout isLoading={exportInvoicesAsync.isLoading || getExportedInvoicesAsync.isLoading}>
            <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
                <table className='w-full border-collapse rounded-lg border-spacing-x-4'>
                    <thead className="font-bold align-bottom">
                        <tr className="text-left">
                            <th className="pl-2 mx-2 w-28">Date/Time<br />Exported</th>
                            <th className="w-32 mx-2">Invoice<br />Month/Year</th>
                            <th className="mx-2">District(s)</th>
                            <th className="mx-2">Status</th>
                            <th className="pr-2 mx-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="rounded-lg">
                        {exportedInvoiceGroupList?.map((eig, idx) => {
                            const invoiceDate = new Date(eig.exportGroup[0].year, eig.exportGroup[0].month - 1); // Adjust for zero-indexing
                            const districtNames = eig.exportGroup.map(g => g.districtName).join(', ');
                            let status = "";
                            let numProcessed = 0;
                            for (let eg of eig.exportGroup) {
                                /*if (r == null)                
                                    Possible stata:
                                    "": (empty) means the request hasn't been sent to QB yet
                                    "Pending"
                                    "Processing"
                                    "Processed"
                                    "Error: <error description>"
                                */
                                if (eg.status.substring(0, 6) === "Error:") {
                                    status = "Error"; //Any error makes the whole export an error
                                    break;
                                } else if (eg.status == "Pending") {
                                    status = "Pending"; //If there's at least 1 pending, the whole export is pending                                 
                                } else if (eg.status == "Processing") {
                                    status = "Processing"; //If there's at least 1 processing, the whole export is processing                                 
                                } else if (eg.status == "Processed") {
                                    numProcessed++;
                                }
                            };
                            if ((status == "") && (eig.exportGroup.length > 0) && eig.exportGroup.length == numProcessed) {
                                status = "Processed";
                            }

                            return (
                                <tr key={`exportedInvoiceGroup_${idx}`}
                                    id={`exported-invoice-group-${eig.exportedOn}`}
                                    className="h-20 rounded-lg odd:bg-gray-100"
                                >
                                    <td className="pl-2 mx-2 w-28">{moment(eig.exportedOn).format("MM/DD/yyyy h:mm A")}</td>
                                    <td className="w-32 mx-2">{moment(invoiceDate).format("MMM yyyy")}</td>
                                    <td className="mx-2">{districtNames}</td>
                                    <td className="mx-2">{status}</td>
                                    <td className="pr-2 mx-2 text-right">
                                        <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                            onClick={(e) => navigateToExportDetails(eig)}>
                                            View Details
                                        </p>
                                        <p className='my-2 cursor-pointer text-ptsblue-normal hover:underline'
                                            onClick={(e) => navigateToExportEdit(eig)}>
                                            Edit
                                        </p>
                                    </td>
                                </tr>
                            )
                        }
                        )}
                    </tbody>
                </table>
            </div>
        </Layout>
    );
};

export default InvoiceExports;